import { useState } from "react";
import { styles } from "../styles";
import { hasCapability, isMine } from "../utils/capabilities";
import TicketSignOff from "./TicketSignOff";
import TicketActions from "./TicketActions";
import BackButton from "../components/BackButton";

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Phase 4.2f — standalone Service Tickets dashboard. Lists every ticket
 * across all jobs, filterable by time range, customer, location, equipment,
 * and status (open vs signed), plus keyword search. Each row expands to the
 * full detail and the review/sign/print controls (TicketSignOff).
 */
export default function ServiceTicketsPage({
  serviceTicketsRaw: rawTickets = [],
  customers = [],
  jobsRaw = [],
  actions,
  role,
  capabilities,
  user,
  initialStatus = "all",
  logoUrl = "",
  orgName = "",
  orgAddress = {},
  onBack,
}) {
  const canEditJobs = hasCapability(role, capabilities, "editJobs");
  const canManage = role === "admin" || role === "manager";
  // Techs (and anyone without viewAllServiceTickets) only see their own tickets.
  const serviceTicketsRaw = hasCapability(role, capabilities, "viewAllServiceTickets")
    ? rawTickets
    : rawTickets.filter((t) => isMine(t, user));

  const [weeks, setWeeks] = useState("all");
  const [customerId, setCustomerId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [equipmentId, setEquipmentId] = useState("");
  const [status, setStatus] = useState(initialStatus);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const jobsById = Object.fromEntries(jobsRaw.map((j) => [j.id, j]));
  const allEquipment = customers.flatMap((c) =>
    (c.locations || []).flatMap((l) => l.equipment || [])
  );

  const selectedCustomer = customers.find((c) => c.id === customerId);
  const locationsForCustomer = selectedCustomer?.locations || [];
  const selectedLocation = locationsForCustomer.find((l) => l.id === locationId);
  const equipmentForLocation = selectedLocation?.equipment || [];

  const cutoff =
    weeks === "all"
      ? 0
      : Date.now() - parseInt(weeks, 10) * 7 * 24 * 60 * 60 * 1000;

  const matches = (t) => {
    if (cutoff && new Date(t.workDate || 0).getTime() < cutoff) return false;
    if (customerId && t.customerId !== customerId) return false;
    if (locationId && t.locationId !== locationId) return false;
    if (equipmentId && !(t.equipmentIds || []).includes(equipmentId)) return false;
    if (status === "open" && t.status === "signed") return false;
    if (status === "signed" && t.status !== "signed") return false;
    const term = search.trim().toLowerCase();
    if (term) {
      const job = jobsById[t.jobId];
      const hay = [
        t.summary,
        job?.title,
        ...(t.labor || []).map((l) => l.employeeName),
        ...(t.materials || []).flatMap((m) => [m.name, m.partNumber]),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!hay.includes(term)) return false;
    }
    return true;
  };

  // Sort by most-recent activity. updatedAt is bumped on every write
  // (submit, edit, sign), so it's the right key for a mixed-status list.
  // Fallbacks cover legacy docs missing updatedAt. Each fallback is a full
  // ISO timestamp (except workDate, which is date-only and lowest priority).
  const filtered = serviceTicketsRaw.filter(matches).sort((a, b) => {
    const ak =
      a.updatedAt || a.signedAt || a.submittedAt || a.createdAt || a.workDate || "";
    const bk =
      b.updatedAt || b.signedAt || b.submittedAt || b.createdAt || b.workDate || "";
    return bk.localeCompare(ak);
  });

  const custObj = (id) => customers.find((c) => c.id === id);
  const locObj = (cId, lId) =>
    customers.find((c) => c.id === cId)?.locations?.find((l) => l.id === lId);
  const custName = (id) => customers.find((c) => c.id === id)?.name || "—";
  const locName = (cId, lId) =>
    customers
      .find((c) => c.id === cId)
      ?.locations?.find((l) => l.id === lId)?.name || "—";

  const canSignTicket = (t) =>
    canEditJobs || (jobsById[t.jobId]?.assignedTechs || []).includes(user?.uid);

  const renderRow = (t) => {
    const open = expandedId === t.id;
    const hours = (t.labor || []).reduce((s, l) => s + num(l.hours), 0);
    const eqNames = (t.equipmentIds || [])
      .map((id) => {
        const eq = allEquipment.find((e) => e.id === id);
        return eq ? `${eq.type}${eq.subType ? ` (${eq.subType})` : ""}` : null;
      })
      .filter(Boolean);
    const job = jobsById[t.jobId];
    return (
      <div key={t.id} style={styles.card}>
        <div
          style={{ cursor: "pointer", fontSize: "13px" }}
          onClick={() => setExpandedId(open ? null : t.id)}
        >
          {open ? "▾" : "▸"} <strong>{t.workDate}</strong>
          {" — "}
          <span
            style={{ color: t.status === "signed" ? "#1a7f37" : "#444" }}
          >
            {t.status === "signed" ? "Signed (locked)" : "Open"}
          </span>
          {!t.acknowledged && (
            <span
              style={{
                marginLeft: "8px",
                background: "#fff3cd",
                color: "#9a6b00",
                fontSize: "11px",
                padding: "1px 6px",
                borderRadius: "8px",
                fontWeight: 700,
              }}
            >
              NEW
            </span>
          )}
          {!t.acknowledged && (
            <button
              style={{ ...styles.button, marginLeft: "8px", padding: "2px 8px" }}
              onClick={(e) => {
                e.stopPropagation();
                actions.updateServiceTicket(t.id, {
                  acknowledged: true,
                  acknowledgedAt: new Date().toISOString(),
                  acknowledgedBy: user?.email || "",
                });
              }}
            >
              Acknowledge
            </button>
          )}
          <br />
          <span style={{ fontSize: "12px", color: "#666", marginLeft: "14px" }}>
            {job
              ? `${job.jobNumber ? `${job.jobNumber} — ` : ""}${job.title}`
              : "(job)"}{" "}
            • {custName(t.customerId)} /{" "}
            {locName(t.customerId, t.locationId)} • {hours} hr
          </span>
        </div>

        {open && (
          <div style={{ marginLeft: "14px", marginTop: "6px", fontSize: "13px" }}>
            <div>
              <strong>Work done:</strong> {t.summary || "(no summary)"}
            </div>
            {eqNames.length > 0 && (
              <div>
                <strong>Equipment:</strong> {eqNames.join(", ")}
              </div>
            )}
            <div>
              <strong>Labor:</strong>
              <ul style={{ margin: "2px 0", paddingLeft: "18px" }}>
                {(t.labor || []).map((l) => (
                  <li key={l.id}>
                    {l.employeeName || "—"} — {num(l.hours)} hr
                    {l.date ? ` (${l.date})` : ""}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <strong>Materials:</strong>
              {(t.materials || []).length === 0 ? (
                <span style={{ color: "#666" }}> none</span>
              ) : (
                <ul style={{ margin: "2px 0", paddingLeft: "18px" }}>
                  {t.materials.map((m) => (
                    <li key={m.id}>
                      {m.name || "—"}
                      {m.partNumber ? ` (#${m.partNumber})` : ""} — qty {num(m.qty)} @
                      ${num(m.unitCost)}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <TicketSignOff
              ticket={t}
              job={jobsById[t.jobId]}
              equipmentList={allEquipment}
              actions={actions}
              canSign={canSignTicket(t)}
              customer={custObj(t.customerId)}
              location={locObj(t.customerId, t.locationId)}
              customerName={custName(t.customerId)}
              locationName={locName(t.customerId, t.locationId)}
              logoUrl={logoUrl}
              orgName={orgName}
              orgAddress={orgAddress}
              /* Office-staff page — surface the itemized copy here so
                 admin/manager can pull a costed work order without the
                 per-job view exposing it to techs. */
              canViewCosts={canManage}
            />

            <TicketActions
              ticket={t}
              jobs={jobsRaw}
              actions={actions}
              canManage={canManage}
              role={role}
              capabilities={capabilities}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <BackButton onClick={onBack} />
      <h2>Service Tickets</h2>

      {/* Filters */}
      <div style={styles.card}>
        <input
          style={styles.input}
          placeholder="Search by summary, job, employee, material, part #"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div style={{ marginTop: "6px", fontSize: "13px" }}>
          <label>Time: </label>
          <select value={weeks} onChange={(e) => setWeeks(e.target.value)}>
            <option value="all">All time</option>
            <option value="1">Last 1 week</option>
            <option value="4">Last 4 weeks</option>
            <option value="12">Last 12 weeks</option>
            <option value="52">Last year</option>
          </select>{" "}
          <label>Status: </label>
          {["all", "open", "signed"].map((s) => (
            <button
              key={s}
              style={{
                ...styles.button,
                margin: "0 4px 0 0",
                background: status === s ? "#1a5fb4" : "#f3f4f6",
                color: status === s ? "white" : "#222",
                fontWeight: status === s ? 600 : 500,
              }}
              onClick={() => setStatus(s)}
            >
              {s === "all" ? "All" : s === "open" ? "Unsigned" : "Signed"}
            </button>
          ))}
        </div>
        <div style={{ marginTop: "6px", fontSize: "13px" }}>
          <label>Customer: </label>
          <select
            value={customerId}
            onChange={(e) => {
              setCustomerId(e.target.value);
              setLocationId("");
              setEquipmentId("");
            }}
          >
            <option value="">All</option>
            {[...customers]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
          {customerId && (
            <>
              {" "}
              <label>Location: </label>
              <select
                value={locationId}
                onChange={(e) => {
                  setLocationId(e.target.value);
                  setEquipmentId("");
                }}
              >
                <option value="">All</option>
                {locationsForCustomer.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </>
          )}
          {locationId && (
            <>
              {" "}
              <label>Equipment: </label>
              <select
                value={equipmentId}
                onChange={(e) => setEquipmentId(e.target.value)}
              >
                <option value="">All</option>
                {equipmentForLocation.map((eq) => (
                  <option key={eq.id} value={eq.id}>
                    {eq.type}
                    {eq.subType ? ` (${eq.subType})` : ""}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
      </div>

      <p style={{ fontSize: "12px", color: "#666" }}>
        {filtered.length} ticket(s)
        {(() => {
          const unack = filtered.filter((t) => !t.acknowledged);
          if (unack.length === 0) return null;
          return (
            <>
              {" · "}
              <strong>{unack.length} unacknowledged</strong>
              <button
                style={{ ...styles.button, marginLeft: "8px" }}
                onClick={async () => {
                  if (
                    !window.confirm(
                      `Acknowledge ${unack.length} ticket(s)? This reduces the dashboard "New" counter for everyone in the org.`
                    )
                  )
                    return;
                  for (const t of unack) {
                    await actions.updateServiceTicket(t.id, {
                      acknowledged: true,
                      acknowledgedAt: new Date().toISOString(),
                      acknowledgedBy: user?.email || "",
                    });
                  }
                }}
              >
                Acknowledge All
              </button>
            </>
          );
        })()}
      </p>

      {filtered.length === 0 ? (
        <p style={{ fontSize: "13px", color: "#666" }}>No tickets match.</p>
      ) : (
        filtered.map(renderRow)
      )}
    </>
  );
}
